import { useEffect, useMemo, useState } from "react"
import { useLocation, useSearchParams } from "react-router-dom"

import axios from "axios"
import toast from "react-hot-toast"

import { Modal, Pagination } from "components"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import { getBranchListForFilter } from "helpers/general-helper"
import { CloudDownload } from "components/icons/outline"
import ImportPrice from "pages/product/unit/import-price"

// 200726 (permintaan user): menu Master Harga — daftar HARGA AKTIF per product unit
// untuk satu outlet (harga atalla per-outlet). Filter outlet default = outlet user login.
// 220726: + tambah/edit harga PER MODEL (satu input → semua unit model, via
// POST price-master/model-price). Kolom: model, harga, harga toko, harga lusin,
// harga toko lusin, total harga, jumlah unit. Field harga/harga lusin di modal
// opsional — kosong = pertahankan nilai lama unit.

// Pagination FE: ubah URL API absolut dari paginator jadi URL halaman CMS
// (?page=N digabung param aktif) supaya klik nomor halaman tetap di SPA.
const toLocalPageLinks = (links, searchParams, pathname) => {
  if (!Array.isArray(links)) return links
  return links.map((l) => {
    if (!l?.url) return l
    let pageNum = null
    try { pageNum = new URL(l.url).searchParams.get("page") } catch { pageNum = null }
    if (!pageNum) return { ...l, url: null }
    const next = new URLSearchParams(searchParams)
    next.set("page", pageNum)
    return { ...l, url: `${pathname}?${next.toString()}` }
  })
}

const formatIdr = (value) => {
  if (value === null || value === undefined || value === "") return "-"
  return Number(value).toLocaleString("id-ID")
}

export default function List() {
  const { currentBranch, can, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  const [isPriceImportOpen, setIsPriceImportOpen] = useState(false)

  // 270726 (permintaan user): opsi filter outlet diurutkan ASC by id outlet.
  // di-memo supaya identitasnya stabil (dipakai sbg dependency di bawah).
  const branchOptions = useMemo(
    () => [...(getBranchListForFilter() || [])].sort(
      (a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0)
    ),
    []
  )

  // Default outlet = outlet user yang login (currentBranch); URL param menang bila ada.
  const defaultOutletId = currentBranch?.id ? String(currentBranch.id) : ""
  const requestedOutletId = searchParams.get("outlet_id") || defaultOutletId

  // 290726 (laporan user — BUG "harga baru muncul setelah ganti outlet lalu balik"):
  // <select value=X> yang X-nya TIDAK ada di antara <option> membuat browser
  // menampilkan opsi PERTAMA, sementara React tetap memakai X. Akibatnya dropdown
  // terbaca "PT Atalla" padahal fetch memakai outlet user yang tidak ada di daftar
  // (mis. terfilter entitas/akses) → hasil kosong. Begitu user memilih outlet lewat
  // dropdown, URL terisi nilai yang valid dan datanya muncul.
  //
  // Perbaikan: kalau outlet yang diminta tidak ada di daftar opsi, pakai opsi pertama
  // — sehingga yang TAMPIL dan yang DI-FETCH selalu sama.
  const outletId = useMemo(() => {
    if (!branchOptions.length) return requestedOutletId
    const ada = branchOptions.some((b) => String(b?.id) === String(requestedOutletId))
    return ada ? String(requestedOutletId) : String(branchOptions[0].id)
  }, [requestedOutletId, branchOptions])

  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "50"
  const searchParam = searchParams.get("search") || ""

  const [search, setSearch] = useState(searchParam)
  useEffect(() => setSearch(searchParam), [searchParam])

  const { data: resultSet, isLoading, mutate } = useResultSet(
    `${process.env.REACT_APP_INVENTORY_API_URL}`,
    "price-master",
    {
      outlet_id: outletId || undefined,
      search: searchParam || undefined,
      page,
      per_page: perPage,
    }
  )

  const rows = useMemo(() => resultSet?.data ?? [], [resultSet])

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)
    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })
    setSearchParams(nextParams)
  }

  const onSearchSubmit = () => mergeSearchParams({ search, page: "1" })

  // Ekspor harga aktif outlet terpilih (reuse filter outlet di atas).
  const exportPrice = async () => {
    const timestamp = new Date().toISOString()

    if (!outletId) {
      toast.error("Silakan pilih outlet terlebih dahulu")
      return
    }

    toast.loading("Memuat", { id: `toast-${timestamp}` })

    try {
      const res = await axios.get(`${process.env.REACT_APP_INVENTORY_API_URL}/export-price`, {
        responseType: "blob",
        params: { outlet_id: outletId },
      })

      const disposition = res.headers?.["content-disposition"] || ""
      const match = disposition.match(/filename="?([^"]+)"?/i)
      const filename = match?.[1] || `product_unit_price_${Date.now()}.xlsx`

      const blob = new Blob([res.data], {
        type:
          res.headers?.["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })

      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast.success("Export price berhasil", { id: `toast-${timestamp}` })
    } catch (error) {
      const msg = error?.response?.data?.message || "Gagal export price"
      toast.error(msg, { id: `toast-${timestamp}` })
    }
  }

  // 220726: tambah/edit harga per MODEL — satu input diterapkan ke semua unit
  // model tsb (backend: POST price-master/model-price, active_date = hari ini).
  const canEditPrice = can("product-price.import")
  const [priceModal, setPriceModal] = useState(null)
  const [priceSubmitting, setPriceSubmitting] = useState(false)
  const [modelSearch, setModelSearch] = useState("")
  const [modelOptions, setModelOptions] = useState([])
  const [modelSearchLoading, setModelSearchLoading] = useState(false)

  const openAddPrice = () => {
    setModelSearch("")
    setModelOptions([])
    setPriceModal({
      mode: "add",
      modelId: "",
      modelName: "",
      price: "",
      storePrice: "",
      dozenPrice: "",
      dozenStorePrice: "",
    })
  }

  const toPriceInput = (value) =>
    value === null || value === undefined ? "" : String(Math.round(Number(value)))

  const openEditPrice = (row) => {
    setPriceModal({
      mode: "edit",
      modelId: String(row.product_model_id || ""),
      modelName: row.product_model_name || "",
      price: toPriceInput(row.price),
      storePrice: toPriceInput(row.store_price),
      dozenPrice: toPriceInput(row.dozen_price),
      dozenStorePrice: toPriceInput(row.dozen_store_price),
    })
  }

  const closePriceModal = () => {
    if (priceSubmitting) return
    setPriceModal(null)
    setModelSearch("")
    setModelOptions([])
  }

  // Cari model untuk mode tambah (min. 2 huruf, debounce 400ms).
  useEffect(() => {
    if (!priceModal || priceModal.mode !== "add") return undefined
    const term = modelSearch.trim()
    if (term.length < 2) {
      setModelOptions([])
      return undefined
    }
    const timeoutId = setTimeout(async () => {
      try {
        setModelSearchLoading(true)
        const res = await axios.get(`${process.env.REACT_APP_INVENTORY_API_URL}/product/model`, {
          params: { paginate: false, search: term },
        })
        setModelOptions(Array.isArray(res?.data?.data) ? res.data.data : [])
      } catch {
        setModelOptions([])
      } finally {
        setModelSearchLoading(false)
      }
    }, 400)
    return () => clearTimeout(timeoutId)
  }, [modelSearch, priceModal])

  const submitPriceModal = async (event) => {
    event.preventDefault()
    if (!priceModal) return
    if (!outletId) { toast.error("Silakan pilih outlet terlebih dahulu"); return }
    if (!priceModal.modelId) { toast.error("Pilih product model terlebih dahulu"); return }

    const storePrice = Number(priceModal.storePrice)
    const dozenStorePrice = Number(priceModal.dozenStorePrice)
    if (!Number.isFinite(storePrice) || storePrice < 0) { toast.error("Harga toko tidak valid"); return }
    if (!Number.isFinite(dozenStorePrice) || dozenStorePrice < 0) { toast.error("Harga toko lusin tidak valid"); return }

    // Harga & harga lusin opsional: kosong = pertahankan nilai lama unit
    // (unit tanpa harga jatuh ke harga toko — perilaku backend).
    const priceRaw = String(priceModal.price ?? "").trim()
    const dozenPriceRaw = String(priceModal.dozenPrice ?? "").trim()
    const price = priceRaw === "" ? null : Number(priceRaw)
    const dozenPrice = dozenPriceRaw === "" ? null : Number(dozenPriceRaw)
    if (price !== null && (!Number.isFinite(price) || price < 0)) { toast.error("Harga tidak valid"); return }
    if (dozenPrice !== null && (!Number.isFinite(dozenPrice) || dozenPrice < 0)) { toast.error("Harga lusin tidak valid"); return }

    setPriceSubmitting(true)
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/price-master/model-price`,
        {
          outlet_id: Number(outletId),
          product_model_id: priceModal.modelId,
          store_price: storePrice,
          dozen_store_price: dozenStorePrice,
          ...(price !== null ? { price } : {}),
          ...(dozenPrice !== null ? { dozen_price: dozenPrice } : {}),
          updated_by: user?.name || undefined,
        }
      )
      toast.success(res?.data?.message || "Harga model berhasil disimpan")
      setPriceModal(null)
      await mutate()
    } catch (error) {
      toast.error(error?.response?.data?.message || "Gagal menyimpan harga model")
    } finally {
      setPriceSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 p-5 bg-white border border-neutral-200 rounded-2xl lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Master Harga</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Harga aktif per produk untuk outlet terpilih (harga berlaku dengan tanggal aktif terbaru).
          </p>
          {(canEditPrice || can("product-price.import") || can("product-price.export")) && (
            <div className="flex items-center mt-3 space-x-2">
              {canEditPrice && (
                <button
                  type="button"
                  onClick={openAddPrice}
                  className="inline-flex items-center px-4 py-2.5 space-x-2 text-xs font-semibold text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                  <span>Tambah Harga</span>
                </button>
              )}
              {can("product-price.import") && (
                <button
                  type="button"
                  onClick={() => setIsPriceImportOpen(true)}
                  className="inline-flex items-center px-4 py-2.5 space-x-2 text-xs font-semibold transition border border-neutral-200 bg-white text-neutral-800 rounded-xl hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-neutral-200"
                >
                  <span>Impor Harga</span>
                </button>
              )}
              {can("product-price.export") && (
                <button
                  type="button"
                  onClick={exportPrice}
                  className="inline-flex items-center px-4 py-2.5 space-x-2 text-xs font-semibold text-white transition bg-brand-800 rounded-xl active:hover:scale-90"
                >
                  <CloudDownload className="w-4 h-4" stroke={1.5} />
                  <span>Ekspor Harga</span>
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="min-w-[240px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Cari Produk
            </label>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onSearchSubmit() } }}
              placeholder="Nama / SKU produk"
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            />
          </div>

          <div className="min-w-[200px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Outlet
            </label>
            <select
              value={outletId}
              onChange={(event) => mergeSearchParams({ outlet_id: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {branchOptions.map((branch) => (
                <option key={branch.id} value={String(branch.id)}>{branch.name}</option>
              ))}
            </select>
          </div>

          <div className="min-w-[120px]">
            <label className="block mb-2 text-xs font-semibold tracking-wide text-neutral-500">
              Per Halaman
            </label>
            <select
              value={perPage}
              onChange={(event) => mergeSearchParams({ per_page: event.target.value, page: "1" })}
              className="w-full px-3 py-2 border rounded-lg border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-800"
            >
              {[20, 50, 100].map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-hidden bg-white border border-neutral-200 rounded-2xl">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-100">
              <tr>
                <th className="px-4 py-3 font-semibold text-left text-neutral-700">Model</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Harga</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Harga Toko</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Harga Lusin</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Harga Toko Lusin</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Rata-rata Harga</th>
                <th className="px-4 py-3 font-semibold text-right text-neutral-700">Jumlah Unit</th>
                {canEditPrice && (
                  <th className="px-4 py-3 font-semibold text-center text-neutral-700">Aksi</th>
                )}
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-neutral-500">
                    Memuat data harga...
                  </td>
                </tr>
              )}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-neutral-500">
                    Tidak ada harga aktif untuk filter yang dipilih.
                  </td>
                </tr>
              )}

              {!isLoading &&
                rows.map((row) => (
                  <tr key={row.product_unit_id} className="border-t border-neutral-200 hover:bg-brand-50 transition-colors">
                    {/* 210726: harga ditampilkan per MODEL (bukan per unit). Harga
                        aktif diambil dari salah satu unit model (server yg pilih). */}
                    <td className="px-4 py-3 text-neutral-800 max-w-[320px] whitespace-normal break-words leading-5">
                      <div>{row.product_model_name || row.product_unit_name}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatIdr(row.price)}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatIdr(row.store_price)}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatIdr(row.dozen_price)}</td>
                    <td className="px-4 py-3 text-right text-neutral-800">{formatIdr(row.dozen_store_price)}</td>
                    {/* 220726 (permintaan user): rata-rata harga = jumlah harga toko
                        aktif semua unit model ini ÷ jumlah unit (dulu: total). */}
                    <td className="px-4 py-3 text-right text-neutral-800">
                      {Number(row.unit_count) > 0 && row.total_store_price != null
                        ? formatIdr(Math.round(Number(row.total_store_price) / Number(row.unit_count)))
                        : "-"}
                    </td>
                    <td className="px-4 py-3 text-right text-neutral-800">{row.unit_count ?? "-"}</td>
                    {canEditPrice && (
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => openEditPrice(row)}
                          disabled={!row.product_model_id}
                          title={
                            row.product_model_id
                              ? "Edit harga semua unit model ini"
                              : "Unit tanpa model — edit lewat detail product unit"
                          }
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-brand-800 rounded-lg hover:bg-brand-900 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 border-t border-neutral-200">
          <Pagination
            links={toLocalPageLinks(resultSet?.links, searchParams, location.pathname)}
            from={resultSet?.from}
            to={resultSet?.to}
            total={resultSet?.total}
          />
        </div>
      </div>

      {can("product-price.import") && (
        <ImportPrice
          isOpen={isPriceImportOpen}
          onClose={() => setIsPriceImportOpen(false)}
          onSuccess={() => mutate()}
        />
      )}

      {/* 220726: modal tambah/edit harga per MODEL. */}
      <Modal
        isOpen={!!priceModal}
        onClose={closePriceModal}
        title={
          priceModal?.mode === "add"
            ? "Tambah Harga per Model"
            : `Edit Harga — ${priceModal?.modelName || ""}`
        }
        content={(
          <form onSubmit={submitPriceModal} className="mt-6 space-y-5 text-xs">
            <p className="text-neutral-500">
              Harga berlaku mulai hari ini dan diterapkan ke <b>semua unit</b> dari model
              terpilih pada outlet ini. Harga dasar & MRP unit tidak diubah.
            </p>

            {priceModal?.mode === "add" && (
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Product Model</label>
                {priceModal.modelId ? (
                  <div className="flex items-center justify-between px-3 py-3 border border-neutral-200 rounded-xl">
                    <span className="font-semibold text-neutral-800">{priceModal.modelName}</span>
                    <button
                      type="button"
                      onClick={() => setPriceModal((prev) => ({ ...prev, modelId: "", modelName: "" }))}
                      className="text-neutral-500 hover:text-neutral-800"
                    >
                      Ganti
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={modelSearch}
                      onChange={(event) => setModelSearch(event.target.value)}
                      placeholder="Ketik min. 2 huruf nama model..."
                      className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                    />
                    {(modelSearchLoading || modelSearch.trim().length >= 2) && (
                      <div className="overflow-y-auto border divide-y max-h-40 border-neutral-100 rounded-xl divide-neutral-100">
                        {modelSearchLoading && (
                          <div className="px-3 py-2 text-neutral-400">Mencari…</div>
                        )}
                        {!modelSearchLoading && modelOptions.length === 0 && (
                          <div className="px-3 py-2 text-neutral-400">Model tidak ditemukan.</div>
                        )}
                        {!modelSearchLoading &&
                          modelOptions.slice(0, 30).map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() =>
                                setPriceModal((prev) => ({
                                  ...prev,
                                  modelId: String(m.id),
                                  modelName: m.long_name || m.name,
                                }))
                              }
                              className="block w-full px-3 py-2 text-left hover:bg-neutral-50"
                            >
                              {m.long_name || m.name}
                            </button>
                          ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Harga</label>
                <input
                  type="number"
                  min="0"
                  value={priceModal?.price ?? ""}
                  onChange={(event) =>
                    setPriceModal((prev) => ({ ...prev, price: event.target.value }))
                  }
                  placeholder="Kosong = ikut nilai lama"
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Harga Toko</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={priceModal?.storePrice ?? ""}
                  onChange={(event) =>
                    setPriceModal((prev) => ({ ...prev, storePrice: event.target.value }))
                  }
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Harga Lusin</label>
                <input
                  type="number"
                  min="0"
                  value={priceModal?.dozenPrice ?? ""}
                  onChange={(event) =>
                    setPriceModal((prev) => ({ ...prev, dozenPrice: event.target.value }))
                  }
                  placeholder="Kosong = ikut nilai lama"
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>
              <div className="space-y-1">
                <label className="block font-medium text-neutral-700">Harga Toko Lusin</label>
                <input
                  type="number"
                  min="0"
                  required
                  value={priceModal?.dozenStorePrice ?? ""}
                  onChange={(event) =>
                    setPriceModal((prev) => ({ ...prev, dozenStorePrice: event.target.value }))
                  }
                  className="block w-full px-3 py-3 text-xs border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="submit"
                disabled={priceSubmitting}
                className="px-5 py-3 text-xs font-semibold text-white bg-brand-800 rounded-xl hover:bg-brand-900 disabled:opacity-60"
              >
                {priceSubmitting ? "Menyimpan..." : "Simpan"}
              </button>
              <button
                type="button"
                disabled={priceSubmitting}
                onClick={closePriceModal}
                className="px-5 py-3 text-xs font-semibold border border-neutral-200 rounded-xl text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
              >
                Batal
              </button>
            </div>
          </form>
        )}
      />
    </div>
  )
}
